/**
 * The frame every step of Make a room sits in: the page head (back to New room,
 * the title, Finish later), the rail of steps on the left with each done step's
 * line and the talk's own tile, and the step's card on the right.
 *
 * What the rail says comes from the server's board, so a reload lands on the same
 * rail. The steps themselves are the same seven (or four, for a live) the board
 * has always walked; only how they look is the redesign's.
 */
import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import { Icon } from "../../components/ui/Icon";
import { cx } from "../../components/ui/cx";
import { BOARD_STEPS, LIVE_STEPS } from "../progress";
import "../../styles/make.css";

export interface RailTalk {
  title: string;
  /** "1.4 GB · 38:24" */
  meta: string;
  poster: string | null;
}

interface MakeFrameProps {
  kind: "rec" | "live";
  /** The step on screen, by index into the kind's steps. */
  current: number;
  /** The furthest step the draft has reached. */
  reached: number;
  /** The line under each done step, by index; empty for none. */
  lines: readonly string[];
  talk?: RailTalk | null;
  /** True once a draft exists: the head offers Finish later instead of Cancel. */
  started: boolean;
  onFinishLater?: () => void;
  children: ReactNode;
}

export function MakeFrame({ kind, current, reached, lines, talk, started, onFinishLater, children }: MakeFrameProps) {
  const live = kind === "live";
  const labels = live ? LIVE_STEPS.map((s) => s.label) : BOARD_STEPS.map((s) => s.label);
  const title = live ? "A new live room" : "A new room from a recording";
  return (
    <div className="page wide nbwizpage">
      <Link className="back" to="/rooms/new">
        <Icon name="left" /> New room
      </Link>
      <div className="ph">
        <div>
          <h1>{title}</h1>
        </div>
        {started ? (
          <button type="button" className="btn" onClick={onFinishLater}>
            <Icon name="clock" /> Finish later
          </button>
        ) : (
          <Link className="btn q" to="/rooms">
            Cancel
          </Link>
        )}
      </div>
      <div className="nbwiz">
        <aside className="card nbrail" aria-label="The steps">
          <ol className="nbsteps">
            {labels.map((label, i) => {
              const done = i < reached;
              const on = i === current;
              return (
                <li
                  key={label}
                  className={cx("nbst", done && "done", on && "on", i > reached && "later")}
                  {...(on ? { "aria-current": "step" as const } : {})}
                >
                  <span className="n">{done ? <Icon name="check" /> : i + 1}</span>
                  <span className="sp">
                    <span className="l">{label}</span>
                    {done && lines[i] ? <em>{lines[i]}</em> : null}
                  </span>
                </li>
              );
            })}
          </ol>
          {talk ? (
            <div className="nbrail-talk">
              <TalkPoster url={talk.poster} />
              <div>
                <b>{talk.title}</b>
                <span>{talk.meta}</span>
              </div>
            </div>
          ) : null}
        </aside>
        <div className="nbwmain">{children}</div>
      </div>
    </div>
  );
}

/** The talk's poster, or a clean placeholder when there is none or it does not load: never a broken image. */
function TalkPoster({ url }: { url: string | null }) {
  const [failed, setFailed] = useState<string | null>(null);
  if (url && failed !== url) return <img src={url} alt="" onError={() => setFailed(url)} />;
  return (
    <span className="nbposter-none">
      <Icon name="video" />
    </span>
  );
}

/** The head of a step's card: "Step 4 of 7", the step's own question, an optional line under it. */
export function StepHead({ n, of, title, sub }: { n: number; of: number; title: ReactNode; sub?: string }) {
  return (
    <div className="nbw-top">
      <div className="eyebrow wine">
        Step {n} of {of}
      </div>
      <h2 className="nbw-h">{title}</h2>
      {sub ? <p className="nbw-sub">{sub}</p> : null}
    </div>
  );
}
